/**
 * Shared plumbing for procedural sound packs: an OfflineAudioContext with a
 * Safari-prefixed fallback, and peak normalisation. Extracted once a second
 * pack (nebula.ts) needed the exact same two functions harp.ts already had.
 */

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

export function offlineContext(length: number, sampleRate: number): OfflineAudioContext {
  const Ctor =
    (globalThis as unknown as { OfflineAudioContext?: OfflineCtor; webkitOfflineAudioContext?: OfflineCtor })
      .OfflineAudioContext ??
    (globalThis as unknown as { webkitOfflineAudioContext?: OfflineCtor }).webkitOfflineAudioContext;
  if (!Ctor) throw new Error("OfflineAudioContext unavailable");
  return new Ctor(1, length, sampleRate);
}

/**
 * Trim a rendered buffer to a fixed peak. Summed partials land wherever their
 * phases happen to align, so raw peaks vary note to note and pack to pack;
 * normalising here means a voice's `velocity` denotes the same loudness
 * everywhere, and any future pack only has to match this peak to drop in.
 */
export function normalize(buffer: AudioBuffer, peak: number): void {
  const data = buffer.getChannelData(0);
  let max = 0;
  for (let i = 0; i < data.length; i++) {
    const value = Math.abs(data[i]);
    if (value > max) max = value;
  }
  if (max === 0) return;
  const scale = peak / max;
  for (let i = 0; i < data.length; i++) data[i] *= scale;
}
